import { effect, inject, Injectable, Signal } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { BehaviorSubject, filter, map, Observable, Subject, switchMap, take, tap } from 'rxjs';
import { Tools } from 'src/app/classes/shared/tools';
import { Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { SocketUser } from 'src/app/interfaces/shared/socket';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { dbScopeFor, GLOBAL_ROOM, scopeOf } from './collection-scope.config';
import { LocalStorageService } from './local-storage.service';
import { SyncStateService } from './sync-state.service';

interface UpdateMessage {
  database: string | string[],
  method: string,
  collection: string,
  date: number,
  data: any[]
}

@Injectable({
  providedIn: 'root'
})

export class WebSocketService {
  // Services
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
  private readonly syncStateService: SyncStateService = inject(SyncStateService);
  private readonly tools: Tools = inject(Tools);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly authService: AuthService = inject(AuthService);
  private connLost: boolean = false;
  // Email du dernier user passé au socket : évite de reconstruire la connexion
  // tant que l'identité ne change pas réellement.
  private currentSocketUserEmail: string | null = null;
  private reconnectSub?: import('rxjs').Subscription;
  // Le serveur ne connaît un utilisateur (map `connectedUsers`) qu'après avoir reçu
  // son `join`. Tout event `stream` émis avant fait planter le handler côté serveur
  // (getUser() -> null). On retient donc les emits tant que le join n'est pas parti.
  private roomsReady: boolean = false;
  private roomsReadyWaiters: Array<() => void> = [];
  // Un disconnect initié par le serveur n'est jamais retenté par socket.io :
  // on relance nous-mêmes, avec un backoff pour ne pas boucler à chaud.
  private serverDisconnectAttempts: number = 0;
  private serverReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly MAX_SERVER_RECONNECT_ATTEMPTS = 5;
  private static readonly PLACEHOLDER_IDENTITIES = new Set(['not defined', 'undefined', 'null', 'anonymous']);

  // Variables
  user: SocketUser = {
    name: "No Name",
    email: "Not defined",
    avatar: "/img/default-avatar.png"
  };

  // autoConnect: false — cette instance n'a pas de query.user : le serveur la
  // laisse connectée en tant qu'"Anonymous", sans présence ni room. C'est une
  // connexion pour rien. Ce n'est qu'un placeholder jusqu'au premier setUser().
  socket: Socket = new Socket({
    url: environment.API_URL.replace(/^http/, 'ws'),
    options: {
      // transports: ['websocket'],
      // upgrade: false,
      timeout: 20000,
      autoConnect: false,
    },
  });

  // Suit l'instance courante de `socket` : setUser() en crée une nouvelle à chaque
  // connexion utilisateur. Tout abonnement pris AVANT ce remplacement (ex. un service
  // construit très tôt, avant la fin du login) doit continuer à recevoir les events —
  // d'où le switchMap dans getUpdateRaw() plutôt qu'un binding figé sur `this.socket`.
  private readonly socket$ = new BehaviorSubject<Socket>(this.socket);

  connectedUsers: SocketUser[] = [];
  database: string = "";
  eventId?: string | undefined;
  // Deux catégories de listeners : les collections 'tenant' sont vidées à chaque
  // changement de projet (setDatabase), les 'global' (voir collection-scope.config.ts)
  // survivent — elles ne dépendent pas du projet actif.
  private tenantListeners = new Map<string, Subject<any>>();
  private globalListeners = new Map<string, Subject<any>>();
  // Rooms actuellement rejointes côté serveur, du point de vue du client (pas une
  // vérité absolue : remise à zéro à chaque (re)connexion, cf. resetAndSyncRooms()).
  private joinedRooms = new Set<string>();

  // Signals
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;

  constructor() {
    const storedDatabase: string | null = this.localStorageService.getItem('database');

    this.watchConnectedUser();
    this.watchDisconnect();

    // 🎯 Effect pour changer de database automatiquement
    effect(() => {
      const project = this.currentProject();

      if (project?.database) {
        this.setDatabase(project.database);
      } else if (storedDatabase) {
        this.setDatabase(storedDatabase);
      }
    });
  }

  /**
   * Branche le socket sur l'identité courante de l'AuthService. Le signal
   * currentUser est alimenté par les deux flux de connexion (email/password ET
   * SSO Microsoft), contrairement au localStorage qui n'est rempli que par le
   * flux email/password — d'où le "No Name" en SSO auparavant.
   */
  watchConnectedUser() {
    effect(() => {
      const user: User = this.authService.currentUser();
      // defaultUser a un email vide : on attend qu'un vrai user soit présent.
      if (!user?.email) {
        return;
      }

      if (this.currentSocketUserEmail === user.email) {
        return;
      }
      this.currentSocketUserEmail = user.email;

      this.setUser({
        id: user.id || user._id,
        name: `${user.firstName} ${user.lastName}`,
        email: `${user.email}`,
        avatar: user.avatar || "/img/default-avatar.png"
      });
    });
  }

  disconnect() {
    this.socket.disconnect();
  }

  /**
   * Base de la clé de cache/syncState pour une collection : la base du projet
   * actif pour une collection 'tenant', la room globale fixe pour 'global'
   * (une collection globale ne doit pas voir son cache fragmenté par projet).
   */
  private dbScopeFor(collection: string): string {
    return dbScopeFor(collection, this.database);
  }

  private listenersFor(collection: string): Map<string, Subject<any>> {
    return scopeOf(collection) === 'global' ? this.globalListeners : this.tenantListeners;
  }

  /**
   * Source de vérité unique : la room globale est toujours désirée, la room tenant
   * ne l'est que si un projet est actif. Le seul endroit à toucher pour ajouter une
   * future room (ex. une room par équipe) — join()/leave() se déduisent tout seuls.
   */
  private desiredRooms(): string[] {
    return this.database ? [GLOBAL_ROOM, this.database] : [GLOBAL_ROOM];
  }

  /** Aligne les rooms rejointes sur desiredRooms() : ne (re)joint/quitte que ce qui a changé. */
  private syncRooms(): void {
    const desired = new Set(this.desiredRooms());

    this.joinedRooms.forEach(room => {
      if (!desired.has(room)) {
        this.socket.emit('leave', room);
      }
    });

    desired.forEach(room => {
      if (!this.joinedRooms.has(room)) {
        this.socket.emit('join', room);
      }
    });

    this.joinedRooms = desired;
  }

  /**
   * À appeler après un (re)connect : le serveur ne se souvient plus de nos rooms
   * précédentes, donc on oublie notre suivi local avant de resynchroniser — sinon
   * syncRooms() croirait à tort que tout est déjà rejoint et n'émettrait rien.
   */
  private resetAndSyncRooms(): void {
    this.joinedRooms.clear();
    this.syncRooms();
    this.markRoomsReady();
  }

  /** Libère les events `stream` mis en attente : le `join` vient d'être émis. */
  private markRoomsReady(): void {
    this.roomsReady = true;
    this.roomsReadyWaiters.splice(0).forEach(resolve => resolve());
  }

  /**
   * Résout dès que le `join` de la connexion courante est parti. Garantit
   * l'ordre join -> stream, y compris quand un composant émet depuis son propre
   * handler 'connect' (enregistré avant celui de ce service, donc appelé avant).
   */
  private whenJoined(): Promise<void> {
    if (this.roomsReady) return Promise.resolve();

    return new Promise<void>(resolve => this.roomsReadyWaiters.push(resolve));
  }

  /** Vrai tant que le socket courant est réellement connecté. */
  isConnected(): boolean {
    return !!this.socket.ioSocket?.connected;
  }

  /**
   * 🎯 Écoute centralisée des updates et dispatch vers les Subjects
   */
  initUpdate() {
    this.socket.fromEvent<UpdateMessage>('update').pipe(
      tap((msg: UpdateMessage) => {
        // Deep defense : if the collection is not global, check if the database is in the list of databases to update
        if (scopeOf(msg.collection) !== 'global') {
          const rooms = Array.isArray(msg.database) ? msg.database : [msg.database];
          if (!rooms.includes(this.database)) return;
        }

        const collectionKey = msg.collection.trim().toLowerCase();
        const dbScope = this.dbScopeFor(msg.collection);
        let key = dbScope + "_" + msg.collection;
        let processedData: any;

        if (msg.method === "DELETE") {
          this.localStorageService.deleteCollectionItem(key, msg.data);
          processedData = this.localStorageService.getItem(key);
        } else if (msg.method === "PUT_BATCH") {
          processedData = this.localStorageService.mergeCollectionItems(key, msg.data);
        } else {
          processedData = this.localStorageService.appendCollectionItems(key, msg.data);
        }

        this.syncStateService.updateSyncState(dbScope, msg.collection, msg.date, "uptodate");

        const listeners = this.listenersFor(msg.collection);
        if (listeners.has(collectionKey)) {
          listeners.get(collectionKey)!.next(processedData);
        }
      })
    ).subscribe();
  }

  /**
   * 📡 Retourne un Observable pour écouter les updates d'une collection
   */
  getUpdate(collection: string): Observable<any> {
    const collectionKey = collection.trim().toLowerCase();
    const listeners = this.listenersFor(collection);

    if (!listeners.has(collectionKey)) {
      listeners.set(collectionKey, new Subject<any>());
    }

    return listeners.get(collectionKey)!.asObservable();
  }

  /**
   * 📡 Écoute les mises à jour WebSocket SANS toucher au localStorage.
   * Passe par socket$ (et non this.socket directement) pour rester valide même
   * si appelée avant setUser() — le switchMap re-bind automatiquement sur le
   * socket courant à chaque remplacement.
   */
  getUpdateRaw(collection: string): Observable<UpdateMessage> {
    return this.socket$.pipe(
      switchMap((socket) => socket.fromEvent<UpdateMessage>('update')),
      filter((data: UpdateMessage) => {
        const receivedCollection = data.collection.trim().toLowerCase();
        const expectedCollection = collection.trim().toLowerCase();
        return receivedCollection === expectedCollection;
      }),
      tap((msg: UpdateMessage) => {
        this.syncStateService.updateSyncState(this.dbScopeFor(collection), collection, msg.date, "uptodate");
      })
    );
  }

  setUser(user: SocketUser) {
    this.user = user;
    // Seule source de vérité de l'identité portée par le socket courant :
    // reconnectWithCurrentUser() s'en sert pour éviter une reconstruction inutile.
    this.currentSocketUserEmail = user.email;
    this.connLost = true;
    this.roomsReady = false;
    this.clearServerReconnect();
    this.socket.disconnect();

    this.socket = new Socket({
      url: environment.API_URL.replace(/^http/, 'ws'),
      options: {
        query: {
          user: JSON.stringify(user)
        }
      }
    });
    this.socket$.next(this.socket);

    this.connLost = false;
    this.socket.connect();

    this.initUpdate();
    this.watchDisconnect();

    this.socket.fromEvent('connect').pipe(take(1)).subscribe(() => {
      this.resetAndSyncRooms();
    });
  }

  /**
   * Ne reconstruit le socket que si c'est nécessaire. Reconstruire à chaque
   * montage de composant ouvrait une 2e connexion pendant que la 1re se fermait :
   * le `disconnect` de l'ancienne pouvait être traité APRÈS le `join` de la
   * nouvelle et effaçait côté serveur la présence tout juste enregistrée
   * (map vidée, plus aucun `join` ensuite -> crash sur user.camera).
   */
  reconnectWithCurrentUser() {
    const user = this.authService.currentUser();
    if (!user?.email) return;

    const identity: SocketUser = {
      id: user.id || (user as any)._id,
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      avatar: user.avatar || '/img/default-avatar.png',
    };

    if (this.currentSocketUserEmail === user.email && this.isConnected()) {
      // Même utilisateur, socket vivant : on rafraîchit juste l'identité locale
      // (le nom peut avoir changé depuis /users/sync) sans toucher au socket.
      this.user = identity;
      return;
    }

    this.setUser(identity);
  }

  setEvent(event?: string) {
    this.eventId = event;
  }

  setDatabase(database: string) {
    if (this.database === database) {
      return;
    }

    // Seuls les listeners 'tenant' sont propres au projet quitté — les 'global'
    // (ex. Project) doivent continuer à recevoir des updates quel que soit le projet actif.
    this.tenantListeners.forEach(subject => subject.complete());
    this.tenantListeners.clear();

    this.database = database;
    this.syncRooms();
  }


  sendMessage(msg: string) {
    // this.socket.emit('message', msg);
  }

  streamEvent(type: string, payload?: any) {
    if (!this.eventId && type !== 'discover') return;
    // Payload figé maintenant : l'émission peut être différée jusqu'au `join`,
    // et this.eventId aura pu changer entre-temps.
    const message = { event: this.eventId, type, ...payload };

    void this.whenJoined().then(() => this.socket.emit("stream", message));
  }

  /**
   * 'connect' du socket COURANT : passe par socket$ pour rester valide après un
   * setUser() (qui remplace l'instance). Un abonnement figé sur `this.socket`
   * n'aurait plus jamais reçu de 'connect' après une reconstruction.
   */
  onConnect(): Observable<unknown> {
    return this.socket$.pipe(switchMap((socket) => socket.fromEvent('connect')));
  }

  private hasIdentity(value?: string | null): boolean {
    const identity: string = (value ?? '').trim().toLowerCase();

    return !!identity && !WebSocketService.PLACEHOLDER_IDENTITIES.has(identity);
  }

  private isRealParticipant(user?: SocketUser): boolean {
    if (!user || (user.name ?? '').trim().toLowerCase() === 'anonymous') {
      return false;
    }

    return this.hasIdentity(user.id) || this.hasIdentity(user.email);
  }

  getConnectedUsers(): Observable<SocketUser[]> {
    return this.socket$.pipe(
      switchMap((socket) => socket.fromEvent<SocketUser[]>('updateConnectedUsers')),
      map((users: SocketUser[]) => {
        return users.filter(user => this.isRealParticipant(user)).map(user => {
          const { firstName, lastName } = this.tools.extractNames(user.name);

          return {
            ...user,
            firstName,
            lastName
          };
        });
      })
    );
  }

  fromStreamEvent(): Observable<any> {
    return this.socket$.pipe(switchMap((socket) => socket.fromEvent<any>('stream')));
  }

  /**
   * When a user is connected to the same project a message is sent to all users in the room
   * @returns joining user info
   */
  getJoining(): Observable<any> {
    return this.socket.fromEvent<SocketUser>('join').pipe(
      tap((data: SocketUser) => {
        if (!this.connectedUsers.some(u => u.name === data.name)) {
          this.connectedUsers.push(data);
        }

        this.emitConnectedUsers();
      })
    );
  }

  /**
   * When a user is leaving the same project a message is sent to all users in the room
   * @returns leaving user info
   */
  getLeaving(): Observable<any> {
    return this.socket.fromEvent<string>('leave').pipe(
      tap((data: string) => {
        this.connectedUsers = this.connectedUsers.filter(u => u.name !== data);
        this.emitConnectedUsers();
      })
    );
  }

  private emitConnectedUsers() {
    this.socket.emit('updateConnectedUsers', this.connectedUsers);
  }

  on(topic: string): any {
    return this.socket$.pipe(switchMap((socket) => socket.fromEvent(topic)));
  }

  private watchDisconnect() {
    this.reconnectSub?.unsubscribe();
    // Instance capturée : ce chaînage suit le socket sur lequel il a été posé,
    // setUser() rappelle watchDisconnect() pour le nouveau.
    const socket = this.socket;

    this.reconnectSub = socket.fromEvent<string>('disconnect').pipe(
      filter(() => !this.connLost),
      tap((reason) => {
        this.connLost = true;
        this.roomsReady = false;
        console.warn('[ws] Unexpected this.hasDisconnect:', reason);
        this.handleServerDisconnect(socket, reason);
      }),
      switchMap(() => socket.fromEvent('connect').pipe(take(1))),
      tap(() => {
        this.connLost = false;
        this.clearServerReconnect();
        console.warn('[ws] Reconnected — re-joining rooms:', this.desiredRooms());
        this.resetAndSyncRooms();
      })
    ).subscribe();
  }

  /**
   * socket.io ne retente RIEN après un disconnect décidé par le serveur
   * (`client.disconnect()`) : sans reconnexion manuelle le socket reste mort
   * définitivement. Backoff exponentiel pour ne pas marteler un serveur qui
   * rejette notre handshake.
   */
  private handleServerDisconnect(socket: Socket, reason: string): void {
    if (reason !== 'io server disconnect') return;

    this.scheduleServerReconnect(socket);
  }

  /**
   * Se replanifie elle-même : un 2e refus du serveur ré-émet bien 'disconnect',
   * mais le filtre `!this.connLost` de watchDisconnect() l'ignore (on est déjà
   * en état déconnecté). La boucle de retry doit donc s'auto-entretenir.
   * Un 'connect' réussi appelle clearServerReconnect() et annule le timer en vol.
   */
  private scheduleServerReconnect(socket: Socket): void {
    if (this.serverDisconnectAttempts >= WebSocketService.MAX_SERVER_RECONNECT_ATTEMPTS) {
      console.error('[ws] Server keeps refusing the connection — giving up after',
        this.serverDisconnectAttempts, 'attempts');
      return;
    }

    const delay = Math.min(1000 * 2 ** this.serverDisconnectAttempts, 30000);
    this.serverDisconnectAttempts++;
    console.warn('[ws] Server-initiated disconnect — manual reconnect in', delay, 'ms');

    this.cancelServerReconnectTimer();
    this.serverReconnectTimer = setTimeout(() => {
      this.serverReconnectTimer = null;
      if (this.isConnected()) return;
      socket.connect();
      this.scheduleServerReconnect(socket);
    }, delay);
  }

  private cancelServerReconnectTimer(): void {
    if (this.serverReconnectTimer) {
      clearTimeout(this.serverReconnectTimer);
      this.serverReconnectTimer = null;
    }
  }

  /** Reconnexion réussie (ou socket remplacé) : on repart d'un backoff neuf. */
  private clearServerReconnect(): void {
    this.cancelServerReconnectTimer();
    this.serverDisconnectAttempts = 0;
  }

  // async getDashboard(): Promise<any> {
  //   try {
  //     let database = this.getDatabase();
  //     if (this.socket.query.room != database) {
  //       this.disconnect();
  //       this.socket.query.room = database;
  //       this.connect();
  //     }

  //     return this
  //       .fromEvent("dashboard")
  //       .pipe(
  //         map((data: any) => data)
  //       )
  //   } catch (e) {
  //     console.error(e)
  //   }
  // }

  // async detachDashboard(): Promise<any> {
  //   try {
  //     let database = this.getDatabase();

  //     if (this.socket.query.room != database) {
  //       this.disconnect();
  //       this.socket.query.room = database;
  //       this.connect();
  //     }
  //     return this.removeListener("dashboard")
  //   } catch (e) {
  //     console.error(e)
  //   }
  // }

  // async getDashboardReplay(project: any = null): Promise<any> {
  //   try {
  //     let database = project ? project.database : this.getDatabase();

  //     if (this.socket.query.room != database) {
  //       this.disconnect();
  //       this.socket.query.room = database;
  //       this.connect();
  //     }

  //     return this
  //       .fromEvent("dashboardReplay")
  //       .pipe(
  //         map((data: any) => data)
  //       )
  //   } catch (e) {
  //     console.error(e)
  //   }
  // }

  // Listen to planning update recalculation progress
  getPlanningUpdateProgress(): Observable<{ progress: number, total: number }> {
    return this.socket.fromEvent<{ progress: number, total: number }>('updatePlanning');
  }

  getVideoUploadProgress(): Observable<{ uploadId: string, percent: number }> {
    return this.on('videoUploadProgress');
  }

  getAutoLinkProgress(): Observable<{ progress: number, total: number }> {
    return this.socket.fromEvent<{ progress: number, total: number }>('autoLinkProgress');
  }
}
